import { useTranslation } from 'react-i18next';
import {
  AppLinkButton,
  Button,
  ModalComponentWrapper,
} from '@/shared/components';
import { useAppSettingsStore, useMediaQuery } from '@/shared/lib';
import AppleStoreIcon from '@/shared/assets/apple-store.svg?react';
import GooglePlayIcon from '@/shared/assets/google-play.svg?react';
import HuaweiStoreIcon from '@/shared/assets/huawei-store.svg?react';
import {
  type TwoFactorAppEnableModalArgs,
  useTwoFactorSettingsModals,
} from '../model';
import { useTwoFactorAppKey } from '../api/queries';

const TwoFactorAuthAppEnable = ({
  onBack,
  onSuccess,
  onSkip,
  goToCurrentScreen,
  preventOverlayClose,
  showCloseButton,
}: TwoFactorAppEnableModalArgs) => {
  const { t } = useTranslation();
  const { language } = useAppSettingsStore();
  const appLinkLocale = language === 'ru' ? 'ru' : 'en';
  const isMobile = useMediaQuery('(max-width: 40rem)');
  const { openTwoFactorAppCodeModal } = useTwoFactorSettingsModals();
  const { refetchTwoFactorAppKey, isTwoFactorAppKeyFetching } =
    useTwoFactorAppKey({
      enabled: false,
    });
  const localePath =
    'personalAccount.userProfile.securityAndPrivacy.twoFactorAuthorization.twoFactorAuthAppEnable';

  const handleContinue = async () => {
    const { data } = await refetchTwoFactorAppKey();

    if (data && 'key' in data) {
      openTwoFactorAppCodeModal({
        onBack: goToCurrentScreen,
        code: data.key,
        uri: data.uri,
        onSuccess,
        preventOverlayClose,
        showCloseButton,
      });
    }
  };

  return (
    <ModalComponentWrapper
      title={t(`${localePath}.title`)}
      description={t(`${localePath}.description`)}
      onBack={onBack}>
      {isMobile ? (
        <div className="flex flex-col gap-[1.6rem] items-stretch">
          <AppLinkButton
            appLink={`https://apps.apple.com/${appLinkLocale}/app/google-authenticator/id388497605`}
            icon={AppleStoreIcon}
            className="w-full"
          />
          <AppLinkButton
            appLink={`https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&&hl=${appLinkLocale}`}
            icon={GooglePlayIcon}
            className="w-full"
          />
          <AppLinkButton
            appLink={`https://appgallery.huawei.com/app/C111807539`}
            icon={HuaweiStoreIcon}
            label="AppGallery"
            iconClassName="size-10"
            className="w-full"
          />
        </div>
      ) : (
        <div className="w-full flex justify-center">
          <img
            src="/assets/qr-code-to-ga-app.svg"
            className="w-[20rem] mb-[2.4rem]"
            alt="QR-code to app"
          />
        </div>
      )}
      <div className="flex flex-col gap-[1.6rem]">
        <Button
          size="lg"
          onClick={handleContinue}
          loading={isTwoFactorAppKeyFetching}>
          {onSkip ? t(`${localePath}.confirmText`) : t('common.continue')}
        </Button>
        {onSkip && (
          <Button size="lg" onClick={onSkip} variant="secondary">
            {t(`${localePath}.cancelText`)}
          </Button>
        )}
      </div>
    </ModalComponentWrapper>
  );
};

export { TwoFactorAuthAppEnable };
